import colorMappings from '@upsales/components/Utils/colorMappings';
import { CurrencyFormat } from 'App/babel/utils/numberFormat';
import React, { useEffect, useRef, useMemo } from 'react';
import T from 'Components/Helpers/translate';
import { Flex } from '@upsales/components';
import Highcharts from 'highcharts';
import moment from 'moment';

type Props = {
	result: number[];
	turnover: number[];
	years: string[];
	placeholder: boolean;
	currency: string;
	chartOrder?: string;
};

const CHART_COLORS = {
	POSITIVE: colorMappings.get('success-4'),
	VALUE: colorMappings.get('green'),
	NEGATIVE: colorMappings.get('red'),
	SERIES_TEXT: colorMappings.get('grey-5'),
	GRID_LINE: colorMappings.get('grey-6'),
	TEXT: colorMappings.get('grey-10')
} as const;

const extendDataToFiveYears = (years: string[], result: number[], turnover: number[], chartOrder: string) => {
	const extendedYears = [...years];
	const extendedResult = [...result];
	const extendedTurnover = [...turnover];

	while (extendedYears.length < 5) {
		if (chartOrder === 'asc') {
			extendedYears.unshift(
				moment(extendedYears[0].toString() + '-01-01')
					.subtract(1, 'year')
					.year()
					.toString()
			);
			extendedResult.unshift(0);
			extendedTurnover.unshift(0);
		} else {
			extendedYears.push(
				moment(extendedYears[extendedYears.length - 1].toString() + '-01-01')
					.subtract(1, 'year')
					.year()
					.toString()
			);
			extendedResult.push(0);
			extendedTurnover.push(0);
		}
	}

	if (chartOrder === 'asc') {
		return {
			years: extendedYears.reverse(),
			result: extendedResult.reverse(),
			turnover: extendedTurnover.reverse()
		};
	}

	return { years: extendedYears, result: extendedResult, turnover: extendedTurnover };
};

const IndustryInsightsChart: React.FC<Props> = React.memo(
	({ result, turnover, years, placeholder, currency, chartOrder = 'desc' }) => {
		const chartRef = useRef<HTMLDivElement | null>(null);
		const {
			years: extendedYears,
			result: extendedResult,
			turnover: extendedTurnover
		} = useMemo(
			() => extendDataToFiveYears(years, result, turnover, chartOrder),
			[years, result, turnover, chartOrder]
		);

		const chartConfig = useMemo(() => {
			const currencyFormat = new CurrencyFormat(currency);
			return placeholder
				? {
						tooltip: { enabled: false },
						chart: {
							type: 'column',
							backgroundColor: 'transparent',
							margin: [20, 16, 35, 73],
							height: 200
						},
						title: { text: null },
						credits: { enabled: false },
						yAxis: {
							gridLineDashStyle: 'Dash',
							plotLines: [
								{
									value: 0,
									color: CHART_COLORS.GRID_LINE,
									width: 1,
									dashStyle: 'Solid'
								}
							],
							labels: {
								formatter: function (): string {
									return '';
								},
								style: {
									color: CHART_COLORS.TEXT,
									fontFamily: 'Inter'
								}
							},
							title: { text: null }
						},
						xAxis: {
							type: 'category',
							title: { text: null },
							tickWidth: 0,
							tickmarkPlacement: 'on',
							lineWidth: 0,
							labels: {
								y: 10,
								x: -38,
								style: {
									color: CHART_COLORS.TEXT,
									fontFamily: 'Inter'
								}
							},
							categories: [2024, 2023, 2022, 2021, 2020]
						},
						plotOptions: {
							series: {
								borderWidth: 0,
								pointWidth: 48
							},
							column: {
								borderRadius: 4,
								grouping: true,
								groupPadding: 0.15
							}
						},
						series: [
							{
								states: { hover: { enabled: false } },
								showInLegend: false,
								name: T('default.turnover'),
								color: CHART_COLORS.SERIES_TEXT,
								data: [17, 13, 8, 7, 1]
							},
							{
								states: { hover: { enabled: false } },
								showInLegend: false,
								name: T('financials.card.plOperatingResult'),
								color: CHART_COLORS.SERIES_TEXT,
								data: [10, 8, 7, 4, 1]
							}
						]
				  }
				: {
						chart: {
							type: 'column',
							backgroundColor: 'transparent',
							margin: [20, 16, 35, 73],
							height: 200
						},
						title: { text: null },
						credits: { enabled: false },
						yAxis: {
							gridLineDashStyle: 'Dash',
							plotLines: [
								{
									value: 0,
									color: CHART_COLORS.GRID_LINE,
									width: 1,
									dashStyle: 'Solid'
								}
							],
							labels: {
								style: {
									color: CHART_COLORS.TEXT,
									fontFamily: 'Inter'
								},
								formatter: function () {
									if ((this as any).isFirst) {
										return '';
									}
									return currencyFormat.short((this as any).value);
								}
							},
							title: { text: null }
						},
						xAxis: {
							type: 'category',
							title: { text: null },
							tickWidth: 0,
							tickmarkPlacement: 'on',
							lineWidth: 0,
							labels: {
								x: -38,
								style: {
									color: CHART_COLORS.TEXT,
									fontFamily: 'Inter'
								}
							},
							showEmpty: true,
							categories: extendedYears
						},
						plotOptions: {
							series: {
								borderWidth: 0,
								pointWidth: 48
							},
							column: {
								borderRadius: 4,
								grouping: true,
								groupPadding: 0.15
							}
						},
						series: [
							{
								showInLegend: false,
								name: T('default.turnover'),
								color: CHART_COLORS.VALUE,
								data: extendedTurnover
							},
							{
								showInLegend: false,
								name: T('financials.card.plOperatingResult'),
								color: CHART_COLORS.POSITIVE,
								negativeColor: CHART_COLORS.NEGATIVE,
								data: extendedResult
							}
						]
				  };
		}, [placeholder, currency, extendedYears, extendedResult, extendedTurnover]);

		useEffect(() => {
			if (!chartRef.current) return;
			new Highcharts.Chart(chartRef.current, chartConfig as any);
		}, [chartConfig]);

		return <Flex style={{ width: '100%' }} flex={1} ref={chartRef} />;
	}
);

IndustryInsightsChart.displayName = 'IndustryInsightsChart';

export default IndustryInsightsChart;
